import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout';
import { isType } from '@sorare/core/src/lib/gql';

import lineupSilhouette from 'assets/lineup-silhouette.webp';

import type { AddTeamLayout_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const Silhouette = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.4;
  background-image: url(${lineupSilhouette});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  aspect-ratio: 842 / 1258;
  transition: scale ease-in-out 0.2s;
`;

const Wrapper = styled(LinkBox)`
  display: flex;
  flex-direction: column;
  border-radius: var(--double-unit);
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: top center;
  border: 1px solid var(--c-nd-150-opaque);

  background-size: 100% 80%;
  &:hover {
    background-size: 102% 82%;
    & ${Silhouette} {
      scale: 1.04;
    }
  }
  transition: background-size ease-in-out 0.2s;

  &.fullHeight {
    height: 100%;
  }
  &.spread {
    min-height: 562px;
  }
  &.inline {
    min-height: 340px;
  }
`;

const InnerWrapper = styled(Vertical)`
  flex: 1;
  height: 100%;
  min-height: inherit;
  background: linear-gradient(
    180.74deg,
    rgba(0, 0, 0, 0) 18.9%,
    #000000 89.56%
  );
  border-radius: var(--double-unit);
  isolation: isolate;
  position: relative;
`;

const LighupBgSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
`;

const Header = styled(Horizontal)`
  padding: var(--double-unit);
  background: linear-gradient(
    180deg,
    var(--c-black) 0%,
    rgba(var(--c-rgb-black), 0) 100%
  );
`;
const LightupBg = ({ color }: { color: string }) => {
  return (
    <LighupBgSvg
      width="421"
      height="630"
      viewBox="0 0 421 630"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_f_2_4)">
        <rect
          x="115"
          y="-10.1797"
          width="190.208"
          height="517.247"
          rx="95.1039"
          fill={color}
        />
      </g>
      <defs>
        <filter
          id="filter0_f_2_4"
          x="-55.2558"
          y="-180.436"
          width="530.719"
          height="857.759"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="85.1279"
            result="effect1_foregroundBlur_2_4"
          />
        </filter>
      </defs>
    </LighupBgSvg>
  );
};

type Props = {
  so5LeaderboardGroup: AddTeamLayout_so5LeaderboardGroupInterface;
  lineupStyle: LineupStyle;
  className?: string;
};

export const AddTeamLayout = ({
  so5LeaderboardGroup,
  children,
  lineupStyle,
  className,
}: PropsWithChildren<Props>) => {
  const getHeaderBackgroundUrl = () => {
    if (
      isType(so5LeaderboardGroup, 'So5League') &&
      so5LeaderboardGroup.name === 'weekly'
    ) {
      return so5LeaderboardGroup.allSeasonsStadiumUrl;
    }
    if (so5LeaderboardGroup.seasonalities.length === 0) {
      return so5LeaderboardGroup.headerbackgroundUrl;
    }
    return so5LeaderboardGroup.seasonalities.includes(
      So5LeaderboardSeasonality.IN_SEASON
    )
      ? so5LeaderboardGroup.inSeasonHeaderbackgroundUrl
      : so5LeaderboardGroup.allSeasonsStadiumUrl;
  };

  return (
    <Wrapper
      className={classNames(lineupStyle, className)}
      style={{
        backgroundImage: `url(${getHeaderBackgroundUrl()})`,
      }}
    >
      <LightupBg color={so5LeaderboardGroup.primaryColor} />
      <Silhouette />
      <InnerWrapper>
        <Header>
          <img
            src={so5LeaderboardGroup.iconUrl}
            alt=""
            width={40}
            height={40}
          />
          <Vertical gap={0}>
            <LabelM bold>{so5LeaderboardGroup.displayName}</LabelM>
            <LabelM color="var(--c-nd-600)">
              {so5LeaderboardGroup.leagueSummary}
            </LabelM>
          </Vertical>
        </Header>
        {children}
      </InnerWrapper>
    </Wrapper>
  );
};

AddTeamLayout.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment AddTeamLayout_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      iconUrl
      seasonalities
      primaryColor: color(variant: PRIMARY)
      headerbackgroundUrl: headerBackgroundUrl
      inSeasonHeaderbackgroundUrl: headerBackgroundUrl(seasonality: IN_SEASON)
      allSeasonsStadiumUrl: stadiumUrl(seasonality: ALL_SEASONS)
      displayName
      leagueSummary
      ... on So5League {
        slug
        name
      }
    }
  ` as TypedDocumentNode<AddTeamLayout_so5LeaderboardGroupInterface>,
};
